// B-161 / B-149: whose browser state is this? Every per-user thing this browser keeps (the check-in queue, Browse answers,
// hidden cards, the draft plan pointer, ...) belongs to ONE user id. When the session's user changes (Delete my data or
// sign-out in another tab, a new anonymous visitor, a sign-in to another account), what the old user left here must go,
// or another tab shows the old account's outings and its answers teach the new account (DB-AUDIT 10/15, taste_events 292-294).
//
// The rule:
// - sessionStorage `mg_owner` = the user id this tab's per-user keys belong to; localStorage `mg_owner` = the same for the
//   browser's per-user keys. Each is checked whenever the session's user is known (every auth event, every other-tab
//   storage change) and when a cache is read (`ownerNow()` reads the saved session synchronously).
// - A different user than the recorded owner (or no user at all after one) = the old user's per-user keys are removed and
//   every registered in-memory store resets (`onOwnerChange`). The first user seen in a tab with no recorded owner simply
//   becomes the owner (nothing to clear; this also adopts browsers from before this rule).
// - Device keys stay: the theme, the calendar app choice, the review key, sign-in in progress, attribution, the session.
// - A sign-in coming back from Google (mg_signin_pending set) keeps the browser keys: the server moves the visitor's rows to
//   the account and lib/signin.ts clears what the merge changes; this tab's session-only caches still go.
//
// No imports on purpose: tests/owner_cache_test.ts runs it under Deno.

/** The supabase-js storage key (lib/api.ts `storageKey`). */
export const AUTH_KEY = 'magellan-auth'
export const OWNER_KEY = 'mg_owner'

/** Device keys: not about a person, kept when the person changes. Everything else named mg_* is per-user. */
const KEEP_LOCAL = new Set([OWNER_KEY, 'mg_theme', 'mg_cal_app', 'mg_attr', 'mg_test', 'mg_signin_pending'])
const KEEP_SESSION = new Set([OWNER_KEY, 'mg_chunk_reload', 'mg_picks_view', 'mg_explore_view', 'mg_signin_show'])
const PENDING_SIGNIN = 'mg_signin_pending'

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>
const ls = (): Store | null => { try { return globalThis.localStorage ?? null } catch { return null } }
const ss = (): Store | null => { try { return globalThis.sessionStorage ?? null } catch { return null } }
const get = (s: Store | null, k: string) => { try { return s?.getItem(k) ?? null } catch { return null } }
const put = (s: Store | null, k: string, v: string | null) => {
  try { if (v === null) s?.removeItem(k); else s?.setItem(k, v) } catch { /* storage blocked: the in-memory owner still works */ }
}

/** The signed-in user id from the saved session, read synchronously (null = no session). */
export function sessionUserId(): string | null {
  try {
    const raw = get(ls(), AUTH_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as { user?: { id?: unknown }; currentSession?: { user?: { id?: unknown } } } | null
    const id = s?.user?.id ?? s?.currentSession?.user?.id
    return typeof id === 'string' && id ? id : null
  } catch {
    return null
  }
}

// the owner as this tab last saw it (also held in memory for a tab whose storage is blocked)
let mem: string | null | undefined
/** Who this tab's per-user state belongs to right now: the session's user (null = nobody signed in). */
export function ownerNow(): string | null {
  return sessionUserId() ?? (mem === undefined ? null : mem)
}

const resets = new Set<() => void>()
/** Register an in-memory store's reset; it runs whenever the person in this tab changes. */
export function onOwnerChange(f: () => void): () => void {
  resets.add(f)
  return () => { resets.delete(f) }
}

/** Is this a per-user key (named mg_*, not a device key)? */
export const perUserKey = (k: string, local: boolean) => k.startsWith('mg_') && !(local ? KEEP_LOCAL : KEEP_SESSION).has(k)

function dropPerUser(s: Store | null, local: boolean) {
  if (!s) return
  try {
    const keys: string[] = []
    for (let i = 0; i < s.length; i++) { const k = s.key(i); if (k && perUserKey(k, local)) keys.push(k) }
    for (const k of keys) s.removeItem(k)
  } catch { /* nothing to clear */ }
}

/**
 * The session's user is `uid` now (null = signed out). Clears what an earlier user left in this tab and in this browser, and
 * resets the in-memory stores when this tab's person changed. Safe to call any number of times. Returns true on a change.
 */
export function reconcileOwner(uid: string | null, deferResets = false): boolean {
  const s = ss()
  const l = ls()
  const tabWas = get(s, OWNER_KEY) ?? (mem === undefined ? null : mem)
  const tabChanged = tabWas !== null && tabWas !== uid
  if (tabChanged) dropPerUser(s, false)
  put(s, OWNER_KEY, uid)
  mem = uid

  const browserWas = get(l, OWNER_KEY)
  if (browserWas !== null && browserWas !== uid && !get(l, PENDING_SIGNIN)) dropPerUser(l, true)
  put(l, OWNER_KEY, uid)

  const run = () => { for (const f of [...resets]) { try { f() } catch { /* one store failing must not stop the rest */ } } }
  // from inside supabase-js's auth callback a store reset must not call supabase (it can deadlock the auth lock): next tick
  if (tabChanged) { if (deferResets) setTimeout(run, 0); else run() }
  return tabChanged
}

let watching = false
type AuthLike = { onAuthStateChange: (cb: (event: string, session: { user?: { id?: string } | null } | null) => void) => unknown }
/**
 * Start watching (idempotent): every auth event (this tab's and, through supabase-js's BroadcastChannel, every other tab's
 * sign-out, delete or sign-in) and every change another tab makes to the saved session or clears storage.
 */
export function watchOwner(auth: AuthLike) {
  if (watching) return
  watching = true
  reconcileOwner(sessionUserId())
  auth.onAuthStateChange((_e, session) => { reconcileOwner(session?.user?.id ?? null, true) })
  try {
    globalThis.addEventListener?.('storage', (e: Event) => {
      const k = (e as Event & { key?: string | null }).key ?? null
      if (k === null || k === AUTH_KEY) reconcileOwner(sessionUserId())
    })
  } catch { /* no window: tests */ }
}

/** Tests only. */
export function _resetOwnerForTests() { mem = undefined; resets.clear(); watching = false }
